import React from "react";
import type { StudioWithAccess } from "../api/types";
import { useAuth } from "../auth/useAuth";
import { useI18n } from "../i18n/useI18n";
import { useUiPreferences } from "../prefs/useUiPreferences";
import { useStudiosWithAccess } from "../utils/useStudiosWithAccess";

export type GlobalStudioContextValue = {
  allowedStudios: StudioWithAccess[];
  optionsOnlyAllowed: { value: string; label: string; disabled?: boolean; title?: string }[];
  studiosLoaded: boolean;
  studiosLoadFailed: boolean;
  studioContextReady: boolean;
  refetchStudios: () => void;
  /** Gespeicherte Teilmenge (nur zugreifbare IDs), leer = „alle Studios“. */
  selectedStudioIds: string[];
  /** Bei „alle“ die effektive ID-Liste (z. B. Anzeige); API-Queries bitte `appendStudioIdsQuery` nutzen. */
  effectiveStudioIds: string[];
  isAllStudios: boolean;
  setSelectedStudioIds: (ids: string[]) => void;
  /** `studioIds` nur setzen, wenn echte Teilmenge; sonst leerer String. */
  studioIdsQueryParam: string;
  appendStudioIdsQuery: (baseUrl: string) => string;
  /** Kurzlabel für Tooltips/Aria (nicht als Select-Wert). */
  scopeLabel: string;
};

/**
 * Globaler Sichtkontext für Listen/Dashboard: leere Auswahl = alle zugreifbaren Studios.
 * Migriert einmalig aus `dashboardPage.studioIds` bzw. Legacy-`localStorage` `dashboardStudioIds.{accountId}`, wenn `studioContext` noch leer ist.
 */
const GlobalStudioContext = React.createContext<GlobalStudioContextValue | null>(null);

/** Ein globaler Scope besitzt auch nur einen Migrations-/Normalisierungsablauf. */
export function GlobalStudioContextProvider({ children }: { children: React.ReactNode }) {
  const value = useGlobalStudioContextValue();
  return React.createElement(GlobalStudioContext.Provider, { value }, children);
}

export function useGlobalStudioContext(): GlobalStudioContextValue {
  const context = React.useContext(GlobalStudioContext);
  if (!context) throw new Error("useGlobalStudioContext requires GlobalStudioContextProvider");
  return context;
}

function useGlobalStudioContextValue(): GlobalStudioContextValue {
  const auth = useAuth();
  const { text } = useI18n();
  const { prefs, isReady, patchPrefs } = useUiPreferences();
  const { allowedStudios, optionsOnlyAllowed, studiosLoaded, studiosLoadFailed, refetch } = useStudiosWithAccess();
  const studioContextReady = isReady && studiosLoaded && !studiosLoadFailed;

  const accountId = auth.info?.accountId ? String(auth.info.accountId) : "";

  const migrationDoneRef = React.useRef(false);

  React.useEffect(() => {
    migrationDoneRef.current = false;
  }, [accountId]);

  React.useEffect(() => {
    if (!isReady || !studiosLoaded || studiosLoadFailed || migrationDoneRef.current) return;
    const rawCtx = prefs.studioContext?.studioIds;
    const hasContext = Array.isArray(rawCtx) && rawCtx.length > 0;
    if (hasContext) {
      migrationDoneRef.current = true;
      return;
    }

    const allowed = new Set(allowedStudios.map((s) => s.id));
    let migrated: string[] = [];

    const fromPrefs = (prefs.dashboardPage?.studioIds ?? []).filter((x): x is string => typeof x === "string" && allowed.has(x));
    if (fromPrefs.length > 0) migrated = fromPrefs;
    else if (accountId) {
      try {
        const raw = localStorage.getItem(`dashboardStudioIds.${accountId}`) || "";
        migrated = raw
          .split(",")
          .map((x) => x.trim())
          .filter((x) => allowed.has(x));
      } catch {
        // ignore
      }
    }

    migrationDoneRef.current = true;
    if (migrated.length > 0) {
      patchPrefs({ studioContext: { studioIds: migrated } });
    }
  }, [isReady, studiosLoaded, studiosLoadFailed, prefs.studioContext?.studioIds, prefs.dashboardPage?.studioIds, allowedStudios, accountId, patchPrefs]);

  const rawStoredIds = prefs.studioContext?.studioIds;

  const selectedStudioIds = React.useMemo(() => {
    if (!Array.isArray(rawStoredIds) || rawStoredIds.length === 0) return [];
    if (!studiosLoaded || studiosLoadFailed) return rawStoredIds.filter((id) => typeof id === "string");
    const allowed = new Set(allowedStudios.map((s) => s.id));
    return rawStoredIds.filter((id) => typeof id === "string" && allowed.has(id));
  }, [rawStoredIds, allowedStudios, studiosLoaded, studiosLoadFailed]);

  React.useEffect(() => {
    if (!isReady || !studiosLoaded || studiosLoadFailed) return;
    if (!Array.isArray(rawStoredIds) || rawStoredIds.length === 0) return;
    if (selectedStudioIds.length === rawStoredIds.length) return;
    patchPrefs({ studioContext: { studioIds: selectedStudioIds.length > 0 ? selectedStudioIds : null } });
  }, [isReady, studiosLoaded, studiosLoadFailed, rawStoredIds, selectedStudioIds, patchPrefs]);

  const isAllStudios = selectedStudioIds.length === 0;

  const effectiveStudioIds = React.useMemo(
    () => (isAllStudios ? allowedStudios.map((s) => s.id) : selectedStudioIds),
    [isAllStudios, allowedStudios, selectedStudioIds]
  );

  const studioIdsQueryParam = React.useMemo(
    () => (isAllStudios ? "" : selectedStudioIds.join(",")),
    [isAllStudios, selectedStudioIds]
  );

  const appendStudioIdsQuery = React.useCallback(
    (baseUrl: string) => {
      if (!studioIdsQueryParam) return baseUrl;
      const enc = encodeURIComponent(studioIdsQueryParam);
      return `${baseUrl}${baseUrl.includes("?") ? "&" : "?"}studioIds=${enc}`;
    },
    [studioIdsQueryParam]
  );

  const setSelectedStudioIds = React.useCallback(
    (ids: string[]) => {
      const allowed = new Set(allowedStudios.map((s) => s.id));
      const cleaned = Array.from(new Set((ids ?? []).map((x) => String(x)).filter((x) => allowed.has(x))));
      patchPrefs({ studioContext: { studioIds: cleaned.length > 0 ? cleaned : null } });
    },
    [allowedStudios, patchPrefs]
  );

  const scopeLabel = React.useMemo(() => {
    if (isAllStudios) return text("All studios");
    if (selectedStudioIds.length === 1) {
      const name = allowedStudios.find((s) => s.id === selectedStudioIds[0])?.name?.trim();
      return name || text("—");
    }
    return text("Global studio scope count").replace("{0}", String(selectedStudioIds.length));
  }, [isAllStudios, selectedStudioIds, allowedStudios, text]);

  return {
    allowedStudios,
    optionsOnlyAllowed,
    studiosLoaded,
    studiosLoadFailed,
    studioContextReady,
    refetchStudios: refetch,
    selectedStudioIds,
    effectiveStudioIds,
    isAllStudios,
    setSelectedStudioIds,
    studioIdsQueryParam,
    appendStudioIdsQuery,
    scopeLabel
  };
}
